import { ChangeDetectionStrategy, Component, inject, OnInit, WritableSignal, signal } from '@angular/core';
import { ContextService } from '../../services/context.service';
import {
  PriorityLevel,
  LicenseType,
  FleetService,
  DriverDto,
  UpdateDriverDto,
  UpdateDriverError,
} from '../../shared/generated/backend';
import { firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderComponent } from '../../loader/loader.component';
import { confirm } from 'devextreme/ui/dialog';

import { SharedModule } from '../../shared/shared.module';
import { TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import {
  DxFormModule,
  DxSortableModule,
  DxTreeViewModule,
  DxDropDownBoxModule,
  DxButtonModule,
  DxToolbarModule,
} from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { DriverRefreshService } from '../../masterdata/driver-list/driver-refresh.service';

interface PriorityLevelWrapper {
  display: string;
  value: PriorityLevel;
}
interface LicenseCategoryWrapper {
  display: string;
  value: LicenseType;
}
@Component({
  selector: 'app-driver-context-view',
  imports: [
    LoaderComponent,
    SharedModule,
    FormsModule,
    DxFormModule,
    DxSortableModule,
    DxTreeViewModule,
    DxDropDownBoxModule,
    DxButtonModule,
    DxToolbarModule,
  ],
  templateUrl: './driver-context-view.component.html',
  styleUrl: './driver-context-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverContextViewComponent implements OnInit {
  readonly driverId: WritableSignal<number | null> = signal(null);
  readonly isLoading: WritableSignal<boolean> = signal(false);
  isDirty!: boolean;
  readonly drivers: WritableSignal<DriverDto[]> = signal([]);
  readonly _priorityLevels = [
    PriorityLevel.Undefined,
    PriorityLevel.VeryHigh,
    PriorityLevel.High,
    PriorityLevel.Normal,
    PriorityLevel.Low,
    PriorityLevel.VeryLow,
  ];
  readonly _licenseTypes = [LicenseType.Small, LicenseType.Middle, LicenseType.Big];
  priorityLevels: Array<PriorityLevelWrapper> = [];
  licenseTypes: Array<LicenseCategoryWrapper> = [];
  formData: DriverDto = {
    employeeNumber: 0,
    firstName: '',
    lastName: '',
    drivingLicence: LicenseType.Small,
    priority: PriorityLevel.Undefined,
    identifier: '',
  };
  dto!: DriverDto;
  type: 'edit' | 'create' = 'edit';

  // Define the data source for the dxTreeView
  treeBoxValue: number[] = [];
  isTreeBoxOpened: boolean = false;

  private contextService = inject(ContextService);
  private fleetService = inject(FleetService);
  private activatedRoute = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private driverRefreshService = inject(DriverRefreshService);

  async ngOnInit() {
    // Making sure that the dropdown values are translated
    this.priorityLevels = await Promise.all(
      this._priorityLevels.map(async (t, _) => {
        const text = await firstValueFrom(this.translateService.get('MASTERDATA.DRIVER.DTO.PRIO_LEVEL.' + t));
        return {
          value: t,
          display: text,
        };
      })
    );
    this.licenseTypes = await Promise.all(
      this._licenseTypes.map(async (t, _) => {
        const text = await firstValueFrom(this.translateService.get('MASTERDATA.DRIVER.DTO.LICENSE_TYPE.' + t));
        return {
          value: t,
          display: text,
        };
      })
    );
    this.activatedRoute.params.subscribe(async params => {
      this.isDirty = false;
      this.isLoading.set(false);
      // console.log('Route Params:', params); // Print the entire params object to the console
      const id = params['driverId'];
      // console.log('Driver ID:', id); // Print the driver ID to the console
      if (id) {
        this.type = 'edit';
        this.driverId.set(id);
        await this.loadDriver();
      } else {
        this.type = 'create';
        this.formData = {
          employeeNumber: 0,
          firstName: '',
          lastName: '',
          drivingLicence: LicenseType.Small,
          priority: PriorityLevel.Undefined,
          identifier: '',
        };
      }
    });
  }

  async loadDriver() {
    this.isLoading.set(true);
    const driverId = this.driverId();
    if (driverId !== null) {
      try {
        this.dto = await firstValueFrom(this.fleetService.getDriver(driverId));
        // console.log('Driver Data:', this.dto); // Print the driver data to the console
      } catch (error) {
        console.error('Error loading driver:', error); // Print the error to the console
      }
    }
    this.formData = { ...this.dto };
    this.isLoading.set(false);
  }

  async delete() {
    const id = this.driverId();
    if (id === null) return;
    const confirmed = await confirm(
      this.translateService.instant('MASTERDATA.DRIVER.CONTEXT.DELETE_PROMPT'),
      this.translateService.instant('MASTERDATA.DRIVER.CONTEXT.DELETE_TITLE')
    );
    if (!confirmed) return;
    this.isLoading.set(true);
    try {
      //await firstValueFrom(this.masterDataService.deleteDriver(id));
      //this.refreshTableAndOpenView(id);
    } catch (
      err: any //eslint-disable-line @typescript-eslint/no-explicit-any
    ) {
      if (err.status === 409) {
        //const innerError = err.error as DeleteDriverError;
        notify(
          {
            message: this.translateService.instant('MASTERDATA.DRIVER.CONTEXT.ERROR_IN_USE_BY'), //+
            //innerError.usedBy.join(', '),
          },
          'error',
          10_000
        );
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async save() {
    if (!this.isDirty) return;

    try {
      this.isLoading.set(true);
      if (this.type === 'edit') {
        const data: UpdateDriverDto = {
          employeeNumber: this.formData.employeeNumber,
          priority: this.formData.priority,
          // Print the driver data to the console
        };
        // console.log('Priority Data:', this.formData.priority);
        await firstValueFrom(this.fleetService.updateDriver(this.dto.employeeNumber, data));
        //this.refreshTableAndOpenView(this.formData.employeeNumber);
        this.driverRefreshService.triggerRefresh(); // Trigger the refresh
        this.close();
      }
    } catch (
      err: any //eslint-disable-line @typescript-eslint/no-explicit-any
    ) {
      if (
        (err.status === 409 && err.error === UpdateDriverError.NameAlreadyUsed) ||
        err.error === UpdateDriverError.NameAlreadyUsed
      ) {
        notify({ message: this.translateService.instant('error message') }, 'error', 5_000);
      } else if (err.status === 409 && err.error === UpdateDriverError.IdAlreadyUsed) {
        notify({ message: this.translateService.instant('error message') }, 'error', 5_000);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  async refreshTableAndOpenView(employeeNumber?: number) {
    // console.log('refreshTableAndOpenView called with employeeNumber:', employeeNumber);
    this.contextService.closeContextView();
    await this.router.navigateByUrl('masterdata/drivers', { onSameUrlNavigation: 'reload' });
    this.contextService.openContextView(`masterdata/drivers`);
    if (employeeNumber !== undefined) {
      // console.log('Opening context view for employeeNumber:', employeeNumber);
      this.contextService.openContextView(`masterdata/drivers/driver/${employeeNumber}`);
    }
  }

  close() {
    this.contextService.closeAllDriverContextViews();
  }
}
